// src/components/SEO.js
import { useEffect } from 'react';
import { SITE_DESCRIPTION } from '../utils/seoText';

const SEO = ({
  title = 'Doorloopjes',
  description = SITE_DESCRIPTION,
  image,
  url,
  type = 'website',
  structuredData,
  noindex = false
}) => {
  useEffect(() => {
    const siteUrl = 'https://doorloopjes.nl';
    const fullUrl = url ? `${siteUrl}${url}` : `${siteUrl}${window.location.pathname}`;
    const defaultImage = `${siteUrl}/images/og-default.png`;
    const ogImage = image || defaultImage;

    // Update title
    document.title = title;

    // Helper function to update or create meta tags
    const setMetaTag = (attr, attrValue, content) => {
      let element = document.querySelector(`meta[${attr}="${attrValue}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, attrValue);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Helper function to update or create link tags
    const setLinkTag = (rel, href) => {
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    // Standard Meta Tags
    setMetaTag('name', 'description', description);
    setLinkTag('canonical', fullUrl);

    // noindex voor pagina's zonder eigen inhoud (404, onbekende slug).
    // Bij navigatie naar een geldige pagina moet de tag weer weg, anders
    // blijft hij in de SPA staan en verdwijnt de hele site uit de index.
    const robotsTag = document.querySelector('meta[name="robots"]');
    if (noindex) {
      setMetaTag('name', 'robots', 'noindex, follow');
    } else if (robotsTag) {
      robotsTag.remove();
    }

    // Open Graph / Facebook / LinkedIn
    setMetaTag('property', 'og:title', title);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:image', ogImage);
    setMetaTag('property', 'og:url', fullUrl);
    setMetaTag('property', 'og:type', type);
    setMetaTag('property', 'og:site_name', 'Doorloopjes');

    // Twitter Cards
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', title);
    setMetaTag('name', 'twitter:description', description);
    setMetaTag('name', 'twitter:image', ogImage);

    // Structured Data (JSON-LD) — supports single object or array
    // Remove existing JSON-LD scripts
    document.querySelectorAll('script[type="application/ld+json"][data-seo]').forEach(el => el.remove());

    if (structuredData) {
      const items = Array.isArray(structuredData) ? structuredData : [structuredData];
      items.forEach((data, i) => {
        const script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute('data-seo', 'true');
        script.textContent = JSON.stringify(data);
        document.head.appendChild(script);
      });
    }
  }, [title, description, image, url, type, structuredData, noindex]);

  return null;
};

export default SEO;
